/**
 * Custom Hooks for Assessment Points
 * Provides React hooks for managing evaluation criteria/points system
 */

import { useCallback } from 'react';
import { useResourceCollection, useResourceById, useCreateResource, useUpdateResource, useDeleteResource, ResourceState, MutationState } from './useResource';
import { assessmentPointsService } from '../services';
import { AssessmentPoint } from '../types';
import { ResourceQueryOptions } from '../services/resources.service';

/**
 * Hook to fetch all assessment points
 */
export function useAssessmentPoints(
  options?: ResourceQueryOptions
): ResourceState<AssessmentPoint> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      assessmentPointsService.getAll(opts) as unknown as Promise<{ data: AssessmentPoint[]; meta?: any }>,
    []
  );
  return useResourceCollection<AssessmentPoint>(fetchFn, options);
}

/**
 * Hook to fetch a single assessment point by ID
 */
export function useAssessmentPoint(id: number | null, options?: ResourceQueryOptions) {
  return useResourceById<AssessmentPoint>((id) => assessmentPointsService.getById(id, options), id);
}

/**
 * Hook to create a new assessment point
 */
export function useCreateAssessmentPoint(onSuccess?: () => void): MutationState<AssessmentPoint> {
  return useCreateResource<AssessmentPoint>(assessmentPointsService.create, onSuccess);
}

/**
 * Hook to update an existing assessment point
 */
export function useUpdateAssessmentPoint(onSuccess?: () => void): MutationState<AssessmentPoint> {
  return useUpdateResource<AssessmentPoint>(assessmentPointsService.update, onSuccess);
}

/**
 * Hook to delete an assessment point
 */
export function useDeleteAssessmentPoint(onSuccess?: () => void): MutationState<void> {
  return useDeleteResource(assessmentPointsService.delete, onSuccess);
}

